// @givmo/board — "Record the result on the roster", its pure parts (CBG-YR3-ELECT; the backend's
// contract `board-terms.md` §7). The dialog (`record-result-dialog.tsx`) renders these.
//
// Board never reads a motion's words or a consent's text for names (plan §3.6): the officer CHOOSES
// whom the record elected. What the dialog prefills is a labeled suggestion, stored only when saved
// (§7.4, I1):
//   * Start — the meeting's day (or the consent's execution day); the officer sets the effective date.
//   * End — from the term length the board recorded, the day before the anniversary
//     (`endAfterYears`), with that source shown; once the officer changes the end, their date stands
//     and the source goes. With no length recorded, empty.
//   * Class — the person's current term's class, when it is still one of the board's.
//   * Counts toward the limit — ticked; shown only with a limit recorded, beside the board's own count.
//   * An officer's "Held from" the same day, "Held to" from the officers' recorded term length.
// The body names the record by exactly one id and each person once per list (§7.1).

import type {
  BoardDirectorClassOut,
  BoardOfficerTitleKey,
  BoardRosterResultRecord,
  OrgBoardMemberOut,
} from '@givmo/api-client';
import type { CatalogTitle } from '../roster/officer-roles';
import { countedTerms, currentTerm, endAfterYears } from '../roster/terms-decode';
import { termsWords as tw } from '../roster/terms-words';

export interface ResultRules {
  lengthYears: number | null;
  lengthCitation: string | null;
  limit: number | null;
  limitCitation: string | null;
  officerYears: number | null;
  officerCitation: string | null;
}

export interface DirectorDraft {
  seatId: string;
  name: string;
  startsOn: string;
  endsOn: string;
  /** The end is still the one the board's recorded length gives from the start. */
  endFromLength: boolean;
  classId: string | null;
  counts: boolean;
  /** The seat's terms already counted toward the limit. */
  countedBefore: number;
}

export interface OfficerDraft {
  seatId: string;
  name: string;
  /** The title's choice key (`CatalogTitle.key`), or `null` until one is chosen. */
  titleKey: string | null;
  heldFrom: string;
  heldTo: string;
  heldToFromLength: boolean;
}

/** The record the result is linked to, as the body names it. */
export type ResultSource = { motionId: string } | { consentId: string };

function lengthEnd(start: string, years: number | null): string {
  return years !== null && start.length > 0 ? (endAfterYears(start, years) ?? '') : '';
}

/** A director's row, prefilled from the record's day and the board's recorded rules. */
export function directorDraft(
  seat: OrgBoardMemberOut,
  day: string,
  rules: ResultRules,
  classes: readonly BoardDirectorClassOut[],
): DirectorDraft {
  const current = currentTerm(seat);
  const keepsClass = classes.some(
    (row) => row.id === current?.class_id && row.status !== 'retired',
  );
  const end = lengthEnd(day, rules.lengthYears);
  return {
    seatId: seat.id,
    name: seat.display_name,
    startsOn: day,
    endsOn: end,
    endFromLength: end.length > 0,
    classId: keepsClass ? (current?.class_id ?? null) : null,
    counts: true,
    countedBefore: countedTerms(seat),
  };
}

/** A new start: the end follows it while it is still the length's. */
export function withStart(
  draft: DirectorDraft,
  startsOn: string,
  rules: ResultRules,
): DirectorDraft {
  if (!draft.endFromLength) return { ...draft, startsOn };
  const end = lengthEnd(startsOn, rules.lengthYears);
  return { ...draft, startsOn, endsOn: end, endFromLength: end.length > 0 };
}

/** The officer's own end: it stands, and the length's source line goes. */
export function withEnd(draft: DirectorDraft, endsOn: string): DirectorDraft {
  return { ...draft, endsOn, endFromLength: false };
}

/** An officer's row, prefilled the same way from the officers' recorded term length. */
export function officerDraft(
  seat: OrgBoardMemberOut,
  day: string,
  rules: ResultRules,
): OfficerDraft {
  const end = lengthEnd(day, rules.officerYears);
  return {
    seatId: seat.id,
    name: seat.display_name,
    titleKey: null,
    heldFrom: day,
    heldTo: end,
    heldToFromLength: end.length > 0,
  };
}

export function officerWithFrom(
  draft: OfficerDraft,
  heldFrom: string,
  rules: ResultRules,
): OfficerDraft {
  if (!draft.heldToFromLength) return { ...draft, heldFrom };
  const end = lengthEnd(heldFrom, rules.officerYears);
  return { ...draft, heldFrom, heldTo: end, heldToFromLength: end.length > 0 };
}

export function officerWithTo(draft: OfficerDraft, heldTo: string): OfficerDraft {
  return { ...draft, heldTo, heldToFromLength: false };
}

/** What stops a save, in the backend's own words; `null` when nothing does. */
export function resultProblem(
  directors: readonly DirectorDraft[],
  officers: readonly OfficerDraft[],
): string | null {
  if (directors.length === 0 && officers.length === 0) return 'Name at least one person elected.';
  for (const row of directors) {
    if (row.startsOn.length === 0 && row.endsOn.length === 0) return tw.needADate;
    if (row.startsOn.length > 0 && row.endsOn.length > 0 && row.endsOn < row.startsOn)
      return tw.endBeforeStart;
  }
  for (const row of officers) {
    if (row.titleKey === null) return tw.chooseTitle;
    if (row.heldFrom.length > 0 && row.heldTo.length > 0 && row.heldTo < row.heldFrom)
      return tw.heldToBeforeFrom;
  }
  return null;
}

/** The body: the record by exactly one id, a term per director and a title per officer. */
export function resultBody(
  source: ResultSource,
  directors: readonly DirectorDraft[],
  officers: readonly OfficerDraft[],
  catalog: readonly CatalogTitle[],
): BoardRosterResultRecord {
  return {
    ...('motionId' in source ? { motion_id: source.motionId } : { consent_id: source.consentId }),
    directors: directors.map((row) => ({
      board_member_id: row.seatId,
      ...(row.startsOn.length > 0 ? { starts_on: row.startsOn } : {}),
      ...(row.endsOn.length > 0 ? { ends_on: row.endsOn } : {}),
      ...(row.classId !== null ? { class_id: row.classId } : {}),
      counts_toward_limit: row.counts,
    })),
    officers: officers.flatMap((row) => {
      const title = catalog.find((choice) => choice.key === row.titleKey);
      if (title === undefined) return [];
      return [
        {
          board_member_id: row.seatId,
          ...(title.id !== null
            ? { title_id: title.id }
            : { default_key: title.default_key as BoardOfficerTitleKey }),
          ...(row.heldFrom.length > 0 ? { held_from: row.heldFrom } : {}),
          ...(row.heldTo.length > 0 ? { held_to: row.heldTo } : {}),
        },
      ];
    }),
  };
}
